import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import { microsoftConsumerSecurityClient } from 'client-factories/MicrosoftConsumerSecurityClientFactoryV1';
import {
  AggregatedDeviceProtectionHistory,
  AssociatedDevice,
  AssociatedDevicesGetAssociatedDevicesOptionalParams,
  Device,
  DevicesDeleteDeviceOptionalParams,
  DevicesGetDevicesSummaryOptionalParams,
  DevicesSummary,
} from 'mcsclient/models';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';
import { createAsyncApiThunk, createOneOffAsyncApiThunk } from 'store/reduxHelpers';

import { getDefaultDevicesState, IDeviceDataState } from './devicesState';

const initialState: IDeviceDataState = getDefaultDevicesState();

// Remove device
export const removeDeviceDataAsync = createAsyncApiThunk('devices/removeDeviceDataAsync', (deviceId: string) => {
  const options: DevicesDeleteDeviceOptionalParams = {
    destroy: true,
  };
  return microsoftConsumerSecurityClient.devices.deleteDevice(deviceId, options);
});

const handleRemoveDeviceDataAsync = (builder: ActionReducerMapBuilder<IDeviceDataState>): void => {
  builder
    .addCase(removeDeviceDataAsync.pending, (state) => {
      state.deviceRemoveStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(removeDeviceDataAsync.fulfilled, (state, { meta }) => {
      state.deviceRemoveStatus = ApiCallResultStatus.SUCCESS;
      delete state.device[meta.arg];
      delete state.deviceLoadStatus[meta.arg];
    })
    .addCase(removeDeviceDataAsync.rejected, (state, action) => {
      state.deviceRemoveStatus = ApiCallResultStatus.FAILED;
      state.deviceRemoveLoadError = action.error.message;
    });
};

// Get device

export const getDeviceDataAsync = createAsyncApiThunk(
  'devices/getDeviceDataAsync',
  (deviceId: string) => microsoftConsumerSecurityClient.devices.getDevice(deviceId) as Promise<Device>
);

const handleGetDeviceDataAsync = (builder: ActionReducerMapBuilder<IDeviceDataState>): void => {
  builder
    .addCase(getDeviceDataAsync.pending, (state, action) => {
      state.deviceLoadStatus[action.meta.arg] = ApiCallResultStatus.LOADING;
    })
    .addCase(getDeviceDataAsync.fulfilled, (state, action) => {
      state.deviceLoadStatus[action.meta.arg] = ApiCallResultStatus.SUCCESS;
      state.device[action.meta.arg] = action.payload;
    })
    .addCase(getDeviceDataAsync.rejected, (state, action) => {
      state.deviceLoadStatus[action.meta.arg] = ApiCallResultStatus.FAILED;
      state.deviceLastLoadError = action.error.message;
    });
};

// Get devices summary
export const getDevicesSummaryAsync = createOneOffAsyncApiThunk(
  'devices/getDevicesSummaryAsync',
  async () => {
    const params: DevicesGetDevicesSummaryOptionalParams = {
      sortBy: 'Severity',
      sortOrder: 'Desc',
      includeFamily: true,
      includeDeletedDevices: true,
    };
    const response = await microsoftConsumerSecurityClient.devices.getDevicesSummary(params);
    return response as DevicesSummary;
  },
  ({ devices }) => devices.devicesSummaryLoadStatus
);

const handleGetDevicesSummaryAsync = (builder: ActionReducerMapBuilder<IDeviceDataState>): void => {
  builder
    .addCase(getDevicesSummaryAsync.pending, (state) => {
      state.devicesSummaryLoadStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(getDevicesSummaryAsync.fulfilled, (state, action) => {
      state.devicesSummaryLoadStatus = ApiCallResultStatus.SUCCESS;
      state.devicesSummary = action.payload;
    })
    .addCase(getDevicesSummaryAsync.rejected, (state, action) => {
      state.devicesSummaryLoadStatus = ApiCallResultStatus.FAILED;
      state.devicesSummaryLoadError = action.error.message;
    });
};

// Get aggregated device protection history
export const getAggregatedDeviceProtectionHistoryAsync = createAsyncApiThunk(
  'deviceProtectionHistory/getAggregatedDeviceProtectionHistory',
  (deviceId: string) =>
    microsoftConsumerSecurityClient.deviceProtectionHistory.getDeviceProtectionHistory(
      deviceId
    ) as Promise<AggregatedDeviceProtectionHistory>
);

const handleGetAggregatedDeviceProtectionHistoryAsync = (builder: ActionReducerMapBuilder<IDeviceDataState>): void => {
  builder
    .addCase(getAggregatedDeviceProtectionHistoryAsync.pending, (state, action) => {
      state.deviceProtectionHistoryLoadStatus[action.meta.arg] = ApiCallResultStatus.LOADING;
    })
    .addCase(getAggregatedDeviceProtectionHistoryAsync.fulfilled, (state, action) => {
      state.deviceProtectionHistoryLoadStatus[action.meta.arg] = ApiCallResultStatus.SUCCESS;
      state.deviceProtectionHistory[action.meta.arg] = action.payload;
    })
    .addCase(getAggregatedDeviceProtectionHistoryAsync.rejected, (state, action) => {
      state.deviceProtectionHistoryLoadStatus[action.meta.arg] = ApiCallResultStatus.FAILED;
      state.deviceProtectionHistoryLastLoadError = action.error.message;
    });
};

// Get associated devices
export const getAssociatedDevicesAsync = createOneOffAsyncApiThunk(
  'devices/getAssociatedDevicesAsync',
  async () => {
    const params: AssociatedDevicesGetAssociatedDevicesOptionalParams = {
      includeFamilyDevices: false,
    };
    const response = await microsoftConsumerSecurityClient.associatedDevices.getAssociatedDevices(params);
    return response as AssociatedDevice[];
  },
  ({ devices }) => devices.associatedDevicesLoadStatus
);

const handleGetAssociatedDevicesAsync = (builder: ActionReducerMapBuilder<IDeviceDataState>): void => {
  builder
    .addCase(getAssociatedDevicesAsync.pending, (state) => {
      state.associatedDevicesLoadStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(getAssociatedDevicesAsync.fulfilled, (state, action) => {
      state.associatedDevicesLoadStatus = ApiCallResultStatus.SUCCESS;
      state.associatedDevices = action.payload;
    })
    .addCase(getAssociatedDevicesAsync.rejected, (state, action) => {
      state.associatedDevicesLoadStatus = ApiCallResultStatus.FAILED;
      state.associatedDevicesLoadError = action.error.message;
    });
};

export const devicesSlice = createSlice({
  name: 'devices',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    handleRemoveDeviceDataAsync(builder);
    handleGetDevicesSummaryAsync(builder);
    handleGetDeviceDataAsync(builder);
    handleGetAggregatedDeviceProtectionHistoryAsync(builder);
    handleGetAssociatedDevicesAsync(builder);
  },
});

export default devicesSlice.reducer;
